import React, { useRef, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';

const Features = () => {
    const { getFullTranslation } = useLanguage();
    const features = getFullTranslation('features');
    const videoRef = useRef(null);
    const [isAudioMode, setIsAudioMode] = useState(false);

    const handleEnableAudio = () => {
        const video = videoRef.current;
        if (!video || isAudioMode) {
            return;
        }

        const currentTime = video.currentTime || 0;
        setIsAudioMode(true);

        window.requestAnimationFrame(async () => {
            const nextVideo = videoRef.current;
            if (!nextVideo) {
                return;
            }

            nextVideo.currentTime = currentTime;

            try {
                await nextVideo.play();
            } catch (error) {
                // Keep controls visible even if autoplay is blocked after source switch.
            }
        });
    };

    return (
        <div id="features" className="max-w-7xl mx-auto px-6 py-20 scroll-mt-24">
            <div className="flex justify-center mb-16">
                <div className="flex text-yellow-400 text-3xl">
                    ★★★★★
                </div>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-between gap-16">
                {/* Left Side: Mockup Image */}
                <div className="w-full md:w-1/2">
                    <div className="w-full aspect-[4/3] bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-100 relative overflow-hidden">
                        <div className="flex h-full w-full items-center justify-center rounded-xl border border-gray-200 bg-white shadow">
                            <div className="relative h-full max-h-[560px] w-full max-w-[315px] overflow-hidden rounded-[1.75rem] bg-black shadow-lg">
                                <video
                                    ref={videoRef}
                                    key={isAudioMode ? 'audio' : 'muted'}
                                    className="h-full w-full object-contain"
                                    src={isAudioMode ? '/videos/promo-web-audio.mp4' : '/videos/promo-web.mp4'}
                                    autoPlay
                                    loop
                                    muted={!isAudioMode}
                                    playsInline
                                    controls={isAudioMode}
                                    preload="metadata"
                                />
                                {!isAudioMode && (
                                    <button
                                        type="button"
                                        onClick={handleEnableAudio}
                                        className="absolute inset-x-3 bottom-3 inline-flex items-center justify-center gap-2 rounded-full bg-black/70 px-4 py-3 text-sm font-medium text-white backdrop-blur transition hover:bg-black/80"
                                    >
                                        <span className="text-base">▶</span>
                                        播放完整视频（含声音）
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side: Text & Icons */}
                <div className="w-full md:w-1/2">
                    <h2 className="text-4xl font-bold text-gray-900 mb-6">{features.title}</h2>
                    <p className="text-lg text-gray-600 mb-10 leading-relaxed max-w-lg">
                        {features.description}
                    </p>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {/* Features Icons */}
                        <div className="flex flex-col items-center justify-center bg-gray-50 rounded-xl p-4 border border-gray-100 hover:-translate-y-1 transition duration-200">
                            <div className="text-3xl mb-3 h-10 flex items-center">📸</div>
                            <span className="text-sm font-medium text-gray-700">{features.photoRecognition}</span>
                        </div>

                        <div className="flex flex-col items-center justify-center bg-gray-50 rounded-xl p-4 border border-gray-100 hover:-translate-y-1 transition duration-200">
                            <div className="text-3xl mb-3 h-10 flex items-center">🔥</div>
                            <span className="text-sm font-medium text-gray-700">{features.calorieEstimation}</span>
                        </div>

                        <div className="flex flex-col items-center justify-center bg-gray-50 rounded-xl p-4 border border-gray-100 hover:-translate-y-1 transition duration-200">
                            <div className="text-3xl mb-3 h-10 flex items-center">🥗</div>
                            <span className="text-sm font-medium text-gray-700">{features.nutritionAnalysis}</span>
                        </div>

                        <div className="flex flex-col items-center justify-center bg-gray-50 rounded-xl p-4 border border-gray-100 hover:-translate-y-1 transition duration-200">
                            <div className="text-3xl mb-3 h-10 flex items-center">🤖</div>
                            <span className="text-sm font-medium text-gray-700">{features.aiAdvisor}</span>
                        </div>
                    </div>
                    <p className="text-sm text-gray-400 mt-6">{features.accuracyNote}</p>
                </div>
            </div>
        </div>
    );
};

export default Features;
